// POS-0034 Stage 2/3: generic "fresh reason + Manager/Owner PIN" approval dialog. Composes the
// existing ManagerPinModal (unchanged, reused as-is) behind a short reason step — used for both
// the shared-phone override flow and the legacy-phone temporary-skip flow, so the UI pattern for
// "a Manager or Owner must authorize this, with a reason, every time" stays in one place.
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ManagerPinModal from "@/components/ManagerPinModal";
import { apiError } from "@/lib/api";

export function PinReasonDialog({ open, onOpenChange, title, description, submitLabel = "Authorize", onConfirm }) {
  const [reason, setReason] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [err, setErr] = useState("");

  const close = () => { setReason(""); setErr(""); setShowPin(false); onOpenChange(false); };

  const proceed = () => {
    if (!reason.trim()) { setErr("A reason is required."); return; }
    setErr("");
    setShowPin(true);
  };

  const handlePinSubmit = async (pin) => {
    try {
      await onConfirm(reason.trim(), pin);
      close();
    } catch (e) {
      throw new Error(apiError(e?.response?.data?.detail) || "Could not authorize this request.");
    }
  };

  return (
    <>
      <Dialog open={open && !showPin} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="max-w-sm" data-testid="pin-reason-dialog">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs">Reason *</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Spouse shares the same household phone" data-testid="pin-reason-input" />
            {err && <p className="text-xs text-destructive" data-testid="pin-reason-error">{err}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={close} data-testid="pin-reason-cancel">Cancel</Button>
            <Button type="button" onClick={proceed} data-testid="pin-reason-continue">Continue to PIN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ManagerPinModal open={open && showPin} onClose={() => setShowPin(false)} onSubmit={handlePinSubmit}
        title={submitLabel} reason={reason} />
    </>
  );
}
